import { useMemo } from "react";
import { useLocation,useNavigate } from "react-router-dom";
import { reportsBackGroupForPath } from "../../pages/reports/reportsCatalog";
import { settingsBackGroupForPath, settingsListForEntryPath } from "../../pages/settings/settingsCatalog";
import type { LocalizedText } from "../i18n/i18n";
import type { BackInfo } from "./backNavigationOrigin";
import { originBackInfo } from "./backNavigationOrigin";
import { adminNavigationBackTarget } from "../../../../../../shared/frontend/admin/adminNavigationCatalog";
import { textFor } from "../i18n/i18n";
import { memberAdminCatalog } from "../layout/adminNavigationCatalog";
import { ROUTE_ADMIN, ROUTE_ADMIN_IMPORT, ROUTE_ADMIN_IMPORT_ADAPTERS, ROUTE_ADMIN_IMPORT_MERGES, ROUTE_ADMIN_IMPORT_RUNS, ROUTE_ADMIN_SETTINGS, ROUTE_COMMUNICATION, ROUTE_FINANCE, ROUTE_FINANCE_SEPA_DIRECT_DEBIT_RUNS, ROUTE_FINANCE_SEPA_DIRECT_DEBIT_RUNS_CREATE, ROUTE_LEADS, ROUTE_MEMBER_CARE_DUE, ROUTE_REPORTS_LISTS, ROUTE_SALES, ROUTE_STAFF, ROUTE_TOOLS } from "../constants/appRoutes";

const backToLabel = (titleKey: string): LocalizedText => ({
  de: `${textFor("de", "AdminStructure.back")} ${textFor("de", titleKey)}`,
  en: `${textFor("en", "AdminStructure.back")} ${textFor("en", titleKey)}`,
});

/**
 * Zurück-Ziel der App-Leiste: zuerst die gesetzte Herkunft, sonst die übergeordnete Seite.
 * Top-Level-Menüpunkte (Aufgaben, Kalender, Check-in) haben ohne Herkunft keinen Zurück-Link.
 */
export function useSmartBackNavigation(): {
  backInfo: BackInfo | null;
  goBack: () => void;
  goBackWithHistory: () => void;
} {
  const navigate = useNavigate();
  const location = useLocation();

  const backInfo = useMemo((): BackInfo | null => {
    const path = location.pathname;
    const origin = originBackInfo(location.state);
    if (origin) return origin;

    // Settings navigation
    if (path.startsWith("/settings/")) {
      if (path.startsWith("/settings/group/")) {
        return { path: "/settings", labelKey: "Back to settings" };
      }
      // Eintrag einer Einstellungsliste (`/settings/<liste>/<eintrag>`) → zurück zur Liste (DESIGN-DNA D30).
      const list = settingsListForEntryPath(path);
      if (list) return { path: list.path, labelKey: backToLabel(list.titleKey) };
      // Das Ziel hängt am Pfad, nicht an der Herkunft: Eine Einstellungsseite gehört genau einer
      // Gruppe an, also führt der Zurück-Weg immer dorthin. Die frühere Auswertung von
      // `state.fromGroup` fiel überall dort auf „Einstellungen“ zurück, wo der Routen-State fehlt
      // — nach einem Tabwechsel (`setSearchParams` verwirft ihn), beim Direktsprung aus der
      // Pinleiste, nach einem Reload und bei jedem Deeplink.
      const group = settingsBackGroupForPath(path);
      if (group) {
        return { path: `/settings/group/${group.key}`, labelKey: group.labelKey };
      }
      return { path: "/settings", labelKey: "Back to settings" };
    }

    // Reports navigation
    if (path.startsWith("/reports/")) {
      if (path.startsWith("/reports/group/")) {
        return { path: "/reports", labelKey: "Back to reports" };
      }
      // Eigene Listen haben keine Gruppenseite im Menü: Detail → Liste, Liste → Auswertungen.
      if (path.startsWith("/reports/lists/")) {
        return { path: "/reports/lists", labelKey: "Back to custom lists" };
      }
      if (path === ROUTE_REPORTS_LISTS) {
        return { path: "/reports", labelKey: "Back to reports" };
      }
      // Wie bei den Einstellungen: Gruppe aus dem Katalog, nicht aus dem Routen-State.
      const group = reportsBackGroupForPath(path);
      if (group) {
        return { path: `/reports/group/${group.key}`, labelKey: group.backLabelKey };
      }
      return { path: "/reports", labelKey: "Back to reports" };
    }

    // Finance navigation
    if (path.startsWith("/finance/") && path !== ROUTE_FINANCE) {
      // Special case: create pages should go back to their list page
      if (path === ROUTE_FINANCE_SEPA_DIRECT_DEBIT_RUNS_CREATE) {
        return { path: "/finance/sepa-direct-debit-runs", labelKey: "Back to direct debits" };
      }
      if (path.startsWith("/finance/sepa-direct-debit-runs/") && path !== ROUTE_FINANCE_SEPA_DIRECT_DEBIT_RUNS) {
        return { path: "/finance/sepa-direct-debit-runs", labelKey: "Back to direct debits" };
      }
      // Default: go back to finance home
      return { path: "/finance", labelKey: "Back to finance" };
    }

    // Communication navigation
    if (path.startsWith("/communication/") && path !== ROUTE_COMMUNICATION) {
      return { path: "/communication", labelKey: "Back to Communication" };
    }

    // Tools navigation
    if (path.startsWith("/tools/duplicate-finder/")) {
      return { path: "/tools/duplicate-finder", labelKey: "Back to duplicate finder" };
    }
    if (path.startsWith("/tools/") && path !== ROUTE_TOOLS) {
      return { path: "/tools", labelKey: "Back to tools" };
    }

    // Sales / Leads: Detail → Liste, Liste → Vertrieb
    if (path.startsWith("/leads/") && path !== ROUTE_LEADS) {
      return { path: "/leads", labelKey: "Back to list" };
    }
    // Mitgliederbetreuung: Kontakt → Liste; `/member-care/due` ist selbst eine (gefilterte) Liste.
    if (path.startsWith("/member-care/") && path !== ROUTE_MEMBER_CARE_DUE) {
      return { path: "/member-care", labelKey: "Back to list" };
    }
    if (path === ROUTE_LEADS) {
      return { path: "/sales", labelKey: "Back to sales" };
    }
    if (path.startsWith("/sales/") && path !== ROUTE_SALES) {
      return { path: "/sales", labelKey: "Back to sales" };
    }

    // Personal: Unterseiten → Personal-Übersicht
    if (path.startsWith("/staff/") && path !== ROUTE_STAFF) {
      return { path: "/staff", labelKey: "Back to Staff" };
    }

    // Admin Import: Run-Bearbeitung → zurück zu Import-Runs
    if (path.startsWith("/admin/import/runs/") && path !== ROUTE_ADMIN_IMPORT_RUNS) {
      return { path: "/admin/import/runs", labelKey: "Back to Import runs" };
    }
    if (path.startsWith("/admin/import/adapters/") && path !== ROUTE_ADMIN_IMPORT_ADAPTERS) {
      return { path: "/admin/import/adapters", labelKey: "Back to CenterCom imports" };
    }
    if (path.startsWith("/admin/import/merges/") && path !== ROUTE_ADMIN_IMPORT_MERGES) {
      return { path: "/admin/import/merges", labelKey: "Back to Merge imports" };
    }
    // Admin Import navigation (Gruppe: zurück zur Import-Gruppenseite)
    if (path.startsWith("/admin/import/") && path !== ROUTE_ADMIN_IMPORT) {
      return { path: "/admin/import", labelKey: "Back to Import" };
    }

    const adminTarget = adminNavigationBackTarget(memberAdminCatalog, path);
    if (adminTarget) return { path: adminTarget.path, labelKey: backToLabel(adminTarget.titleKey) };

    // Admin Settings navigation
    if (path.startsWith("/admin/settings/") && path !== ROUTE_ADMIN_SETTINGS) {
      return { path: "/admin/settings", labelKey: "Back to Admin Settings" };
    }

    // Admin navigation: normale Admin-Unterseiten gehen zurück zum Admin-Dashboard (Hubs oben abgedeckt).
    if (path.startsWith("/admin/") && path !== ROUTE_ADMIN) {
      return { path: "/admin", labelKey: "Navigation.backToOverview" };
    }

    return null;
  }, [location.pathname, location.state]);

  const goBack = () => {
    if (backInfo) {
      navigate(backInfo.path);
    }
  };

  const goBackWithHistory = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (backInfo) {
      navigate(backInfo.path);
    }
  };

  return {
    backInfo,
    goBack,
    goBackWithHistory
  };
}
